import React, { useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import MuiBox from "../MuiBox";

/**
 * Normalize any incoming value into a valid dayjs instance or null.
 * Accepts: dayjs, Date, ISO string, "HH:mm", "HH:mm:ss", null/undefined.
 */
const toDayjsTime = (value) => {
  if (value == null || value === "") return null;

  // Already a dayjs object
  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value : null;
  }

  // Native Date
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }

  // String: try full parse first, then time-only formats
  if (typeof value === "string") {
    let d = dayjs(value);
    if (d.isValid()) return d;

    // Common time-only formats
    for (const fmt of ["HH:mm", "HH:mm:ss", "hh:mm A", "hh:mm:ss A", "H:mm", "h:mm A"]) {
      d = dayjs(value, fmt);
      if (d.isValid()) return d;
    }
    return null;
  }

  // Number (epoch ms)
  if (typeof value === "number") {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }

  return null;
};

const CustomTimePicker = ({
  label,
  value,
  onChange,
  views,
  error,
  errorMsg,
  onError,
  format = "",
  labelClassName,
  variant,
  className,
  placeholder,
  ampm = true,
  ...rest
}) => {
  const [showMsg, setShowMsg] = useState("");

  useEffect(() => {
    if (error) {
      setShowMsg(errorMsg);
    } else {
      setShowMsg("");
    }
  }, [error, errorMsg]);

  // Always pass a valid dayjs | null to TimePicker
  const normalizedValue = useMemo(() => toDayjsTime(value), [value]);

  // Default to hours + minutes if none are provided
  const resolvedViews = views || ["hours", "minutes"];

  // Determine the appropriate format based on resolvedViews and ampm
  const getFormat = () => {
    if (format) return format;
    if (resolvedViews.includes("seconds")) {
      return ampm ? "hh:mm:ss A" : "HH:mm:ss";
    }
    if (resolvedViews.includes("minutes")) {
      return ampm ? "hh:mm A" : "HH:mm";
    }
    if (resolvedViews.includes("hours")) {
      return ampm ? "hh A" : "HH";
    }
    return ampm ? "hh:mm A" : "HH:mm";
  };

  // Determine sizes based on variant
  const isLg = variant === "lg";
  const inputHeight = isLg ? "40px" : "32px";
  const inputFontSize = isLg ? "0.875rem" : "0.8125rem";

  return (
    <MuiBox sx={{ py: 0 }} className={className}>
      {label && (
        <label
          className={`block font-semibold text-[14px] ${labelClassName || ""}`}
        >
          {label}
        </label>
      )}
      <MuiBox sx={{ mt: 0, pt: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            className="time-picker-custom w-full"
            value={normalizedValue}
            ampm={ampm}
            localeText={
              placeholder
                ? {
                  fieldHoursPlaceholder: () => placeholder.hours ?? "hh",
                  fieldMinutesPlaceholder: () => placeholder.minutes ?? "mm",
                  fieldMeridiemPlaceholder: () => placeholder.meridiem ?? "aa",
                }
                : undefined
            }
            slotProps={{
              textField: {
                error: Boolean(error),
                readOnly: true,
                fullWidth: true,
                sx: {
                  "& .MuiOutlinedInput-root": {
                    height: inputHeight,
                    borderRadius: "5px",
                    backgroundColor: "#fff",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                    width: "100%",

                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid rgba(226, 232, 240, 0.63)",
                    },

                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid #ef4444 !important",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "hsl(240 3.8% 46.1% / 0.4)",
                      opacity: 1,
                    },
                    "&.Mui-focused": {
                      boxShadow:
                        "0 1px 2px 0 rgba(0, 0, 0, 0.05), 0 0 0 3px rgba(203, 213, 225, 0.25)",
                      border: "0.5px solid rgb(210 210 219 / 0.7)",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "0 12px",
                    fontSize: inputFontSize,
                  },
                },
              },
            }}
            onChange={(newValue) => {
              // Always return dayjs | null to the parent
              if (onChange) {
                onChange(
                  newValue && dayjs.isDayjs(newValue) && newValue.isValid()
                    ? newValue
                    : null,
                );
              }
            }}
            views={resolvedViews}
            format={getFormat()}
            onError={(e) => {
              if (onError) {
                onError(e);
              }
              if (
                e === "invalidDate" ||
                e === "disableFuture" ||
                e === "disablePast" ||
                e === "minutesStep" ||
                e === "minTime" ||
                e === "maxTime"
              ) {
                setShowMsg("Please enter a valid time");
              } else {
                setShowMsg(errorMsg || "");
              }
            }}
            emptyLabel={`Enter ${label || "time"}`}
            {...rest}
          />
          {showMsg && (
            <div className="text-[0.8125rem] text-red-500">{showMsg}</div>
          )}
        </LocalizationProvider>
      </MuiBox>
    </MuiBox>
  );
};

export default CustomTimePicker;